/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { generateUrl } from '../../routes/util';
import { getInitiativeTree } from '../../selectors/initiativeTree';
import { Tree, ReportingLevelInterface, ReportingLevelOption } from '../../types/tree';
import { RouteInterface } from '../../types/routes';
import { unloadInitiativeSurveyList } from '../../slice/initiativeSurveyListSlice';
import {
  SelectFactory,
  SelectTypes,
} from '@g17eco/molecules/select/SelectFactory';
import { getBackgroundColor as getBackgroundSelectColor } from '@g17eco/molecules/select/utils';
import { SelectProps as SingleSelectProps } from '@g17eco/molecules/select/SingleSelect';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { ellipsis } from '@utils/index';
import { useAppDispatch } from '@reducers/index';

export const createOptions = (reportingLevelTree: Tree) => {
  const hasReportingLevels = reportingLevelTree.children.length > 0;
  const maxNestedOptions = 15;
  const reportingLevelOptions: ReportingLevelOption[] = [];

  if (!hasReportingLevels) {
    return reportingLevelOptions;
  }

  const populateReportingLevelOptions = (level: ReportingLevelInterface, nested: number, parentId: string) => {
    // get precise levelId from subsidiary ids
    const [levelId] = level.id.split('_');

    reportingLevelOptions.push({
      value: levelId,
      label: level.name,
      parentId: parentId,
      isHidden: nested > maxNestedOptions,
      nested: nested,
      code: level.code,
    });
    if (level.children && level.children.length > 0) {
      level.children.forEach((child) => populateReportingLevelOptions(child, nested + 1, level.id));
    }
  };
  reportingLevelTree.children.forEach((child) => populateReportingLevelOptions(child, 1, reportingLevelTree.id));

  // remove replicated subsidiaries which are already a children
  return reportingLevelOptions.filter((option) => {
    if (reportingLevelOptions.some((item) => option.code === item.code && option.nested < item.nested)) {
      return false;
    }
    return true;
  });
};

interface ReportingLevelDropdownParams {
  route: RouteInterface;
  initiativeTree?: Tree;
  placeholder?: string;
}

export const ReportingLevelDropdown = (props: ReportingLevelDropdownParams) => {
  const { route, initiativeTree, placeholder = 'Select your Reporting Level' } = props;
  const { initiativeId = '', portfolioId } = useParams<{ initiativeId?: string; portfolioId?: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const baseTree = useSelector(getInitiativeTree);

  const reportingLevelTree = initiativeTree ?? baseTree;
  const reportingLevelOptions = createOptions(reportingLevelTree);

  if (reportingLevelOptions.length === 0) {
    return null;
  }

  if (reportingLevelOptions.length === 1) {
    return (
      <SimpleTooltip text={reportingLevelOptions[0].label}>{ellipsis(reportingLevelOptions[0].label)}</SimpleTooltip>
    );
  }

  const selectedUrlId = portfolioId ?? initiativeId;

  const onReportingLevelChange = (value: any) => {
    // Dropdown value
    const [id] = value.value.split('_');
    dispatch(unloadInitiativeSurveyList()); // This is to force reset of the select report on the summary page
    history.push(generateUrl(route, { initiativeId: id, portfolioId: id }));
  };

  const selectedReportingLevel = reportingLevelOptions.find((o) => selectedUrlId && o.value === selectedUrlId);

  const unhideSelectedOptions = (initiativeId: string) => {
    const option = reportingLevelOptions.find((option) => option.value === initiativeId);
    if (option) {
      // Unhide self
      option.isHidden = false;
      // Unhide all parents
      if (option.parentId) {
        unhideSelectedOptions(option.parentId);
      }
      // Unhide direct children
      reportingLevelOptions
        .filter((option) => option.parentId === initiativeId)
        .forEach((option) => (option.isHidden = false));
    }
  };

  if (selectedReportingLevel && initiativeId) {
    unhideSelectedOptions(initiativeId);
  }

  const nestedPadding = 12;
  const customStyles: SingleSelectProps['styles'] = {
    option: (provided, state: any) => {
      return {
        ...provided,
        minHeight: 32,
        backgroundColor: getBackgroundSelectColor(state),
        display: state.data.isHidden ? 'none' : 'inline-block',
        padding: '6px 12px',
        paddingLeft: `${state.data.nested * nestedPadding}px`,
        fontWeight: state.data.nested === 1 ? 'bold' : 'normal',
        '&:hover': {
          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        },
      };
    },
  };

  return (
    <SelectFactory
      selectType={SelectTypes.SingleSelect}
      placeholder={placeholder}
      className='w-100'
      styles={customStyles}
      onChange={onReportingLevelChange}
      value={selectedReportingLevel ?? null}
      isSearchable={true}
      options={reportingLevelOptions}
    />
  );
};
